import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { fitRem } from '../../lib/fitText';

// Text that always fits the width it is given: it starts at `maxRem` and steps down in rem (so it still follows the user's
// text size) until nothing is cut off, and only falls back to an ellipsis once it reaches `minRem`. Re-fits when the text,
// the column width or the webfont changes. Give it a width to fill (flex-1, w-full, or a block parent).
export default function FitText({
  children,
  className = '',
  maxRem = 1,
  minRem = 0.7,
}: {
  children: ReactNode;
  className?: string;
  maxRem?: number;
  minRem?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const size = fitRem((rem) => {
      el.style.fontSize = `${rem}rem`;
      return el.scrollWidth > el.clientWidth;
    }, maxRem, minRem);
    el.style.fontSize = `${size}rem`;
  }, [maxRem, minRem]);

  // After every render, so a changed name re-fits straight away.
  useLayoutEffect(fit);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    // Webfonts change text width once they load.
    try { void document.fonts?.ready.then(fit); } catch { /* noop */ }
    return () => ro?.disconnect();
  }, [fit]);

  return <span ref={ref} className={`block min-w-0 truncate ${className}`}>{children}</span>;
}
